import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import { isRoundLive } from './lib/roundDisplay'
import type { CompetitionFixtures } from './types'

export type SubscriptionTier = 'BETA' | 'FREE' | 'ORGANIZADOR' | 'LIGA_PRO'

export type MyProfile = {
  id:               string
  displayName:      string
  avatarUrl:        string | null
  subscriptionTier: SubscriptionTier
  globalPushOptOut: boolean
}

export const profileKeys = {
  all: ['profile'] as const,
  me:  () => [...profileKeys.all, 'me'] as const,
}

export const fixturesKeys = {
  all:    ['fixtures'] as const,
  detail: (competitionId: string) => [...fixturesKeys.all, competitionId] as const,
}

export type MySubscription = {
  tier:           SubscriptionTier
  leaguesCreated: number
  leaguesJoined:  number
  leagueLimit:    number | null  // null = unlimited (Liga Pro)
}

export const subscriptionKeys = {
  all:     ['subscription'] as const,
  current: () => [...subscriptionKeys.all, 'current'] as const,
}

export async function fetchMySubscription(): Promise<MySubscription> {
  return apiClient.get<MySubscription>(endpoints.subscriptions.current())
}

export function useMySubscription() {
  return useQuery({
    queryKey:  subscriptionKeys.current(),
    queryFn:   fetchMySubscription,
    staleTime: 60_000,
  })
}

export async function fetchMyProfile(): Promise<MyProfile> {
  return apiClient.get<MyProfile>(endpoints.users.me())
}

export async function fetchCompetitionFixtures(competitionId: string): Promise<CompetitionFixtures> {
  return apiClient.get<CompetitionFixtures>(endpoints.competitions.fixtures(competitionId))
}

export function useMyProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn:  fetchMyProfile,
    staleTime: 5 * 60_000,
  })
}

// The round now carries live scores, not just a fixed calendar, so this can no longer be
// cached for an hour. While any match in the current round is in progress it polls; once the
// round goes quiet the interval stops and normal staleness applies.
const LIVE_POLL_MS = 60_000

export function useCompetitionFixtures(competitionId: string | null) {
  return useQuery({
    queryKey: fixturesKeys.detail(competitionId ?? ''),
    queryFn:  () => fetchCompetitionFixtures(competitionId!),
    enabled:  competitionId !== null,
    staleTime: 60_000,
    refetchInterval: query =>
      isRoundLive(query.state.data?.currentRound) ? LIVE_POLL_MS : false,
  })
}
